import type { DiscoveryHealth } from '#~/agent/discovery/utils.js';
import {
  ALERT_CLOSE_REASON,
  ALERT_ID,
  ALERT_VARIANT,
  createAlertTracker,
  type AlertCloseReason,
  type AlertShowing
} from '#~/analytics/domains/alert-lifecycle.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { ElectronAPI } from '#~/electron/api.js';
import { useLinkedAgentIDSelector } from '#~/electron/identity-store.js';
import { logger } from '#~/logger/index.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import { useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { useProducer, useStore } from '../store/hooks.js';
import { Panel } from '../store/types.js';

// N11 does not depend on the notice incident helper (ENG-4930). Keep its
// in-place cooldown in memory, scoped to the studio and desktop Bridge.
const lastShown = new Map<string, number>();

/** Own N11 for the whole add-camera dialog, including time in manual entry. */
export const useDiscoveryWarning = (): void => {
  const { enabled, loading } = useFeatureFlag('alert_n11_enabled', { defaultValue: true });
  const { track } = useAnalytics();
  const producer = useProducer();
  const roomId = useStore((state) => state.roomId);
  const panel = useStore((state) => state.panel);
  const bulkStatus = useStore((state) => state.source.bulkAddStatus.kind);
  const [agentId] = useLinkedAgentIDSelector();
  const [health, setHealth] = useState<DiscoveryHealth>();
  const [cooldownTick, setCooldownTick] = useState(0);
  const showing = useRef<AlertShowing | undefined>(undefined);
  const skipped = useRef(false);

  const close = useLatestFn((reason: AlertCloseReason) => {
    if (!showing.current) return;
    createAlertTracker(track).trackAlertClosed(showing.current, reason);
    showing.current = undefined;
    producer((draft) => {
      draft.discoveryWarning = undefined;
    });
  });

  useEffect(() => {
    let active = true;
    let pushed = false;
    // Subscribe before reading so an older getter response cannot overwrite a
    // recovery (or failure) pushed while the request is in flight.
    const unsubscribe = ElectronAPI.mDNS.onHealthChanged((next) => {
      pushed = true;
      if (active) setHealth(next);
    });
    void ElectronAPI.mDNS
      .getHealth()
      .then((next) => {
        if (active && !pushed) setHealth(next);
      })
      .catch((error: unknown) => {
        logger.warning('Could not read camera discovery health', error);
      });
    return () => {
      active = false;
      unsubscribe();
      close(ALERT_CLOSE_REASON.DISMISS);
    };
  }, [close]);

  useEffect(() => {
    if (!health) return undefined;
    // When both fail, one callout represents the incident. Retain its original
    // failed method and showing ID until ALL methods recover.
    const variant =
      health.mdns.status === 'failed'
        ? ALERT_VARIANT.MDNS
        : health.scan.status === 'failed'
          ? ALERT_VARIANT.SCAN
          : undefined;
    if (!variant) {
      close(ALERT_CLOSE_REASON.RESOLVED);
      skipped.current = false;
      return undefined;
    }
    if (loading || enabled !== true) {
      // The switch contract skips unresolved incidents instead of queuing them
      // for a later flag response. Switching off also removes an active callout.
      skipped.current = true;
      close(ALERT_CLOSE_REASON.SUPERSEDED);
      return undefined;
    }
    // Scaffold replaces Discover with progress while bulk add is running.
    // An invisible callout must not emit shown or consume the cooldown.
    const discoverVisible =
      panel === Panel.Source && (bulkStatus === 'idle' || bulkStatus === 'done');
    if (showing.current || skipped.current || !discoverVisible) return undefined;
    const key = JSON.stringify([roomId, agentId]);
    const now = Date.now();
    const remaining = 60_000 - (now - (lastShown.get(key) ?? -Infinity));
    if (remaining > 0) {
      // Only unresolved/off flags skip an incident. Cooldown defers it until
      // the boundary, even when an ongoing failure produces no further push.
      const timer = setTimeout(() => setCooldownTick((tick) => tick + 1), remaining);
      return () => clearTimeout(timer);
    }
    const next: AlertShowing = {
      alert_id: ALERT_ID.N11,
      alert_variant: variant,
      showing_id: v4(),
      studio_id: roomId,
      ...(agentId ? { agent_id: agentId } : {})
    };
    showing.current = next;
    lastShown.set(key, now);
    producer((draft) => {
      draft.discoveryWarning = next;
    });
    createAlertTracker(track).trackAlertShown(next);
    return undefined;
  }, [
    health,
    enabled,
    loading,
    panel,
    bulkStatus,
    roomId,
    agentId,
    producer,
    track,
    close,
    cooldownTick
  ]);
};
